/**
 * Salary simulation (legacy "Simulasi gaji"): one person's payslip for any period,
 * with today's rules and their attendance so far. Nothing is saved. Without payroll
 * access it simulates the viewer's own pay (legacy personal/employee/simulation).
 */
import { keepPreviousData, useMutation, useQuery } from '@tanstack/react-query';
import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { Icon } from '../components/Icon.tsx';
import { Avatar, Button, Callout, Empty, Field, PageHead, Panel, Segmented } from '../components/ui.tsx';
import { ApiError, get, post, qs } from '../lib/api.ts';
import { dateRange, idr, todayIn } from '../lib/format.ts';
import { useMe } from '../lib/session.ts';
import { nextPeriod } from './Payroll.tsx';

export const simulationSearch = (s: Record<string, unknown>) => ({ employeeId: typeof s.employeeId === 'string' ? s.employeeId : undefined });

interface Line {
  componentId: string;
  name: string;
  kind: 'earning' | 'deduction';
  source: string;
  amount: string;
  calculated: boolean;
  shown: boolean;
  formula: string | null;
  note: string | null;
}
interface Result {
  period: { from: string; to: string };
  basicSalary: string;
  lines: Line[];
  totals: { earnings: string; deductions: string; net: string };
  incomeTax: { method: string; ptkp: { code: string }; withheld: string; bracket: { name: string } | null } | null;
  prorate: { changedOn: string } | null;
  metrics: { totalWorkDays: number; totalWorkingDays: number; daysAbsent: number; minLate: number; minEarlyOut: number; minOvertime: number };
  warnings: string[];
}
interface Person {
  id: string;
  name: string;
  employeeNo: string | null;
  jobPosition: string | null;
  officeName: string | null;
  status: string;
}

/** The pay period (26th → 25th) running today, or the one before it. */
function periodAt(back: 0 | 1) {
  const current = nextPeriod(todayIn());
  return back ? nextPeriod(`${current.from.slice(0, 8)}01`) : current;
}

export function Simulation() {
  const me = useMe().data!;
  const search = useSearch({ strict: false }) as { employeeId?: string };
  const navigate = useNavigate();
  // Without payroll access (or in My view) it is always the viewer's own pay.
  const own = !me.access.payroll;
  const [person, setPerson] = useState<Person | null>(null);
  const subjectId = own ? me.employee?.id : person?.id;
  const [which, setWhich] = useState<'current' | 'previous' | 'custom'>('previous');
  const [custom, setCustom] = useState(periodAt(1));
  const [basic, setBasic] = useState('');
  const period = which === 'current' ? periodAt(0) : which === 'previous' ? periodAt(1) : custom;

  // Arriving from a person's Pay & bank tab
  const preset = useQuery({ queryKey: ['sim-person', search.employeeId], queryFn: () => get<Person & { office: { name: string } | null; employment: { jobPosition: { name: string } | null } | null }>(`/api/people/${search.employeeId}`), enabled: !own && !!search.employeeId && person?.id !== search.employeeId });
  useEffect(() => {
    const p = preset.data;
    if (p) setPerson({ id: p.id, name: p.name, employeeNo: p.employeeNo, jobPosition: p.employment?.jobPosition?.name ?? null, officeName: p.office?.name ?? null, status: p.status });
  }, [preset.data]);

  const run = useMutation({
    mutationFn: () => post<Result>('/api/payroll/simulate', { employeeId: subjectId!, periodFrom: period.from, periodTo: period.to, basicSalary: basic.replace(/[^\d]/g, '') || undefined }),
  });
  // Switching between HR view and My view changes whose pay this is.
  const { reset } = run;
  useEffect(() => reset(), [own, reset]);
  const r = run.data;
  const earnings = r?.lines.filter((l) => l.kind === 'earning' && l.calculated && Number(l.amount) !== 0) ?? [];
  const deductions = r?.lines.filter((l) => l.kind === 'deduction' && l.calculated && Number(l.amount) !== 0) ?? [];
  const info = r?.lines.filter((l) => !l.calculated && l.shown && Number(l.amount) !== 0) ?? [];

  if (own && !me.employee) return <Empty title="No employee profile">Your account isn’t linked to an employee record.</Empty>;

  return (
    <div>
      <PageHead
        eyebrow={own ? 'My pay' : 'Payroll'}
        title={
          <>
            Salary <em>simulation.</em>
          </>
        }
        lede={
          own
            ? 'Your payslip for any period, calculated with today’s rules and the attendance recorded so far. Nothing is saved and nobody is notified.'
            : "One person's payslip for any period, calculated with today's rules and the attendance recorded so far. Nothing is saved and nobody is notified."
        }
        aside={
          own ? (
            <Link to="/me" className="btn btn--ghost btn--sm">
              <Icon name="user" />
              My profile
            </Link>
          ) : null
        }
      />
      <div className="grid-12" style={{ alignItems: 'start' }}>
        <div className="col-5 stack">
          {own ? null : <Panel eyebrow="Who" title={person ? person.name : 'Choose a person'}>
            {person ? (
              <div className="row row--between">
                <span className="row" style={{ ['--gap' as string]: '12px' }}>
                  <Avatar name={person.name} size={36} />
                  <span>
                    <span className="mono faint" style={{ display: 'block', fontSize: 12 }}>{person.employeeNo ?? 'No number'}</span>
                    <span className="faint" style={{ fontSize: 'var(--step--1)' }}>{[person.jobPosition, person.officeName].filter(Boolean).join(' · ')}</span>
                  </span>
                </span>
                <Button size="sm" variant="ghost" onClick={() => { setPerson(null); run.reset(); if (search.employeeId) void navigate({ to: '/payroll/simulate', search: {}, replace: true }); }}>
                  Change
                </Button>
              </div>
            ) : (
              <PersonSearch onPick={(p) => { setPerson(p); run.reset(); }} />
            )}
          </Panel>}
          <Panel eyebrow="Period" title={dateRange(period.from, period.to)}>
            <div className="stack">
              <Segmented label="Period" idPrefix="sim-period" value={which} onChange={setWhich} options={[{ id: 'previous', label: 'Last period' }, { id: 'current', label: 'This period' }, { id: 'custom', label: 'Other dates' }]} />
              {which === 'current' ? <p className="faint" style={{ fontSize: 'var(--step--1)' }}>The period is still running: days ahead without attendance count as absent, as in a run calculated today.</p> : null}
              {which === 'custom' ? (
                <div className="grid-12" style={{ gap: 12 }}>
                  <div className="col-6"><Field label="From">{(p) => <input {...p} type="date" className="input" value={custom.from} onChange={(e) => setCustom({ ...custom, from: e.target.value })} />}</Field></div>
                  <div className="col-6"><Field label="To">{(p) => <input {...p} type="date" className="input" value={custom.to} onChange={(e) => setCustom({ ...custom, to: e.target.value })} />}</Field></div>
                </div>
              ) : null}
              <Field label="Try a different basic salary" hint={own ? 'Optional, e.g. to see what a raise would mean. Leave empty to use yours.' : 'Optional. Leave empty to use the one on file.'}>
                {(p) => <input {...p} className="input input--mono" inputMode="numeric" placeholder="e.g. 6500000" value={basic} onChange={(e) => setBasic(e.target.value)} />}
              </Field>
              <Button variant="primary" icon="formula" disabled={!subjectId || (which === 'custom' && (!custom.from || !custom.to || custom.from > custom.to))} loading={run.isPending} onClick={() => run.mutate()}>
                Calculate
              </Button>
              {run.error ? <Callout tone="bad">{run.error instanceof ApiError ? run.error.message : 'The calculation failed.'}</Callout> : null}
            </div>
          </Panel>
        </div>
        <div className="col-7">
          {!r ? (
            <Empty title={subjectId ? 'Pick a period and calculate.' : 'Choose a person to begin.'}>
              The result uses the same engine as payroll runs, including attendance deductions, BPJS, PPh 21 and cooperative savings.
            </Empty>
          ) : (
            <div className="panel" style={{ padding: 'var(--s-6)' }}>
              <div className="row row--between row--wrap">
                <div>
                  <div className="eyebrow">Simulated payslip</div>
                  <div className="display" style={{ fontSize: '1.4rem' }}>{dateRange(r.period.from, r.period.to)}</div>
                </div>
                <dl className="row row--wrap" style={{ ['--gap' as string]: '24px', fontSize: 'var(--step--1)' }}>
                  <div><dt className="eyebrow">Work days</dt><dd className="num">{r.metrics.totalWorkingDays}</dd></div>
                  <div><dt className="eyebrow">Absent</dt><dd className="num">{r.metrics.daysAbsent}</dd></div>
                  <div><dt className="eyebrow">Late</dt><dd className="num">{r.metrics.minLate} min</dd></div>
                  <div><dt className="eyebrow">Overtime</dt><dd className="num">{Math.round(r.metrics.minOvertime / 6) / 10} h</dd></div>
                </dl>
              </div>
              {r.warnings.length ? (
                <div style={{ marginTop: 'var(--s-4)' }}>
                  <Callout tone="warn" title="Check these.">
                    <ul style={{ margin: 0, paddingLeft: 18 }}>{r.warnings.map((w) => <li key={w}>{w}</li>)}</ul>
                  </Callout>
                </div>
              ) : null}
              <div className="payslip-lines" style={{ marginTop: 'var(--s-5)' }}>
                <div>
                  <div className="eyebrow" style={{ marginBottom: 6 }}>Earnings</div>
                  <div className="payslip-line">
                    <span>Basic salary{basic ? <span className="faint"> · tried</span> : null}</span>
                    <span className="num">{idr(r.basicSalary)}</span>
                  </div>
                  {earnings.map((l, i) => <SimLine key={`${l.componentId}-${i}`} l={l} />)}
                </div>
                <div>
                  <div className="eyebrow" style={{ marginBottom: 6 }}>Deductions</div>
                  {deductions.map((l, i) => <SimLine key={`${l.componentId}-${i}`} l={l} minus />)}
                  {!deductions.length ? <p className="faint">No deductions.</p> : null}
                </div>
              </div>
              <div className="payslip-net">
                <div>
                  <div className="eyebrow">Take-home pay</div>
                  {r.incomeTax ? (
                    <div className="faint" style={{ fontSize: 'var(--step--2)', marginTop: 6 }}>
                      PPh 21 {idr(r.incomeTax.withheld)} · {r.incomeTax.ptkp.code}
                      {r.incomeTax.bracket ? ` · ${r.incomeTax.bracket.name}` : ''}
                    </div>
                  ) : null}
                  {r.prorate ? <div className="faint" style={{ fontSize: 'var(--step--2)', marginTop: 4 }}>Salary changed in the period: prorated from {r.prorate.changedOn}.</div> : null}
                </div>
                <span className="display num" style={{ color: 'var(--accent-text)' }}>{idr(r.totals.net)}</span>
              </div>
              {info.length ? (
                <div style={{ marginTop: 'var(--s-5)' }}>
                  <div className="eyebrow" style={{ marginBottom: 6 }}>For information</div>
                  {info.map((l, i) => (
                    <div key={`${l.componentId}-${i}`} className="payslip-line">
                      <span className="muted">{l.name}</span>
                      <span className="num muted">{idr(l.amount)}</span>
                    </div>
                  ))}
                </div>
              ) : null}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function SimLine({ l, minus }: { l: Line; minus?: boolean }) {
  return (
    <div className="payslip-line">
      <span>
        {l.name}
        {l.source === 'employee' ? <span className="faint"> · own</span> : null}
        {l.note ? <span className="faint"> · {l.note}</span> : null}
        {l.formula ? <div className="code-inline" style={{ marginTop: 4, display: 'inline-block' }}>{l.formula}</div> : null}
      </span>
      <span className="num">{minus ? '−' : ''}{idr(l.amount)}</span>
    </div>
  );
}

function PersonSearch({ onPick }: { onPick: (p: Person) => void }) {
  const [q, setQ] = useState('');
  const [debounced, setDebounced] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setDebounced(q.trim()), 220);
    return () => clearTimeout(t);
  }, [q]);
  const list = useQuery({ queryKey: ['sim-search', debounced], queryFn: () => get<{ items: Person[] }>(`/api/people${qs({ q: debounced, size: 8, status: 'all' })}`), enabled: debounced.length >= 2, placeholderData: keepPreviousData });
  const items = (list.data?.items ?? []).filter((p) => p.status === 'active' || p.status === 'onboarding');
  return (
    <div className="stack" style={{ ['--gap' as string]: '8px' }}>
      <div className="search-field">
        <Icon name="search" width={16} height={16} />
        <input className="input" placeholder="Name or employee no." value={q} onChange={(e) => setQ(e.target.value)} aria-label="Search people" autoFocus />
      </div>
      {debounced.length >= 2 ? (
        items.length ? (
          <ul className="list-plain">
            {items.map((p) => (
              <li key={p.id}>
                <button type="button" className="access-row" onClick={() => onPick(p)}>
                  <Avatar name={p.name} size={28} />
                  <span className="access-row__main">
                    <span className="access-row__name">{p.name}</span>
                    <span className="access-row__meta">{[p.employeeNo, p.jobPosition, p.officeName].filter(Boolean).join(' · ')}</span>
                  </span>
                </button>
              </li>
            ))}
          </ul>
        ) : list.isFetching ? null : (
          <p className="faint" style={{ fontSize: 'var(--step--1)' }}>No current employee matches.</p>
        )
      ) : null}
    </div>
  );
}
